import { endOfDay, startOfDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import {
  CreateExperimentDialog,
  ExperimentGroupItem,
} from 'src/dialogs/PricingABTest/CreateExperimentDialog';
import { useListingExperiments } from 'src/hooks/api/useListingExperiments';
import { BasicDialogProps, useBasicDialog } from 'src/hooks/useBasicDialog';
import { IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { ListingExperimentTable } from 'src/tables/ListingExperimentTable/ListingExperimentTable';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Event,
  Listing,
  ListingExperiment,
  ListingGroup,
} from 'src/WebApiController';

import * as styles from '../InventoryEventPage.css';

type InventoryPricingABTestSectionProps = {
  event: Event;
  listings: Listing[];
  createExperimentDialog?: BasicDialogProps;
};

export const InventoryPricingABTestSection: React.FC<
  InventoryPricingABTestSectionProps
> = ({ event, listings, createExperimentDialog }) => {
  const {
    isLoading,
    experiments,
    onUpdateExperiment,
    onCreateExperiment,
    onStopExperiment,
    onRestartExperiment: restartExperimentMutation,
  } = useListingExperiments(event.viagId!);

  const { loginContext } = useAppContext();

  const fallbackExperimentalDialog = useBasicDialog();
  const sanitizedDialog = useMemo(
    () => createExperimentDialog ?? fallbackExperimentalDialog,
    [createExperimentDialog, fallbackExperimentalDialog]
  );

  const [selectedExperiment, setSelectedExperiment] =
    useState<ListingExperiment | null>(null);

  const listingGroups = useMemo(() => {
    return listings.filter(({ isLtGrp }) => isLtGrp) as ListingGroup[];
  }, [listings]);

  const availableListingGroups = useMemo((): ExperimentGroupItem[] => {
    const candidates = listingGroups
      .map(
        (group) =>
          ({
            id: (group as ListingGroup).ltGrpId,
            name: (group as ListingGroup).groupName,
          }) as ExperimentGroupItem
      )
      .concat({ id: 'N/A', name: 'N/A' });

    if (selectedExperiment) {
      return candidates;
    }

    const usedListingGroupIds = experiments?.map(
      ({ listingGroupId }) => listingGroupId ?? 'N/A'
    );

    return candidates.filter(({ id }) => !usedListingGroupIds?.includes(id));
  }, [experiments, listingGroups, selectedExperiment]);

  const defaultValues = useMemo((): ListingExperiment => {
    if (selectedExperiment) {
      return selectedExperiment;
    }
    const current = new Date();
    const startDate = startOfDay(current).toISOString();
    current.setDate(current.getDate() + 7);
    const endDate = endOfDay(current).toISOString();
    return {
      viagogoEventId: event.viagId!,
      listingGroupId: null,
      listingGroupName: null,
      targetPercentage: null,
      discountPercentage: 10,
      startDate,
      endDate,
      insertBy: loginContext?.user?.displayName || '',
    };
  }, [event.viagId, loginContext?.user?.displayName, selectedExperiment]);

  const onResumeExperiment = useCallback(
    (listingGroupId: string | null) => {
      const selected = experiments?.find(
        (experiment) => experiment.listingGroupId === listingGroupId
      );
      setSelectedExperiment(selected ?? null);
      sanitizedDialog.launchDialog();
    },
    [sanitizedDialog, experiments]
  );

  const onRestartExperiment = useCallback(
    (listingGroupId: string | null) => {
      const exp = experiments?.find(
        (experiment) => experiment.listingGroupId === listingGroupId
      );

      if (exp) {
        restartExperimentMutation(exp);
      }
    },
    [restartExperimentMutation, experiments]
  );

  const onCloseDialog = useCallback(() => {
    setSelectedExperiment(null);
    sanitizedDialog.closeDialog();
  }, [sanitizedDialog]);

  return (
    <>
      <div className={styles.tableSection}>
        <ListingExperimentTable
          isLoading={isLoading}
          experiments={experiments}
          listingGroups={listingGroups}
          onResumeExperiment={onResumeExperiment}
          onRestartExperiment={onRestartExperiment}
          onStopExperiment={onStopExperiment}
        />
        <Stack direction="row" gap="m" width="full" alignItems="end">
          <ButtonWithIcon
            icon={
              <PlusIcon fill={IconsFill.textBrand} size={vars.iconSize.m} />
            }
            textContentId={ContentId.FlashSale}
            onClick={() => sanitizedDialog?.launchDialog()}
            style={{ width: 'min-content', whiteSpace: 'nowrap' }}
            variant={'text'}
          />
        </Stack>
      </div>
      <CreateExperimentDialog
        {...sanitizedDialog.dialogProps}
        viagogoEventId={1}
        isLoading={isLoading}
        defaultValues={defaultValues}
        availableGroups={availableListingGroups}
        isUpdate={!!selectedExperiment}
        onCreateExperiment={onCreateExperiment}
        onUpdateExperiment={onUpdateExperiment}
        onClosed={onCloseDialog}
        onCancel={onCloseDialog}
      />
    </>
  );
};
